import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import {
  Activity,
  AlertTriangle,
  Clock3,
  ExternalLink,
  Gauge,
  Loader2,
  RefreshCw,
  Server,
  Wifi,
  type LucideIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  getSystemObservability,
  getOpsAlerts,
  getOpsContainerHistory,
  type ClientObservabilityEvent,
  type OpsAlertRecord,
  type OpsMetricSnapshot,
  type SystemObservabilityContainer,
  type SystemObservabilitySnapshot,
  type WebSocketObservabilityEvent,
} from '../../../lib/api'
import { getAdminAppsPath } from '../../../lib/adminRoutes'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import { AnalyticsNav } from './AnalyticsOverview'
import StatCard from '../../../components/analytics/StatCard'
import TrendChart from '../../../components/analytics/TrendChart'
import { CHART_PALETTE } from '../../../lib/chart-colors'
import AutoRefreshNotice from '../../../components/AutoRefreshNotice'

type Tone = 'critical' | 'warning' | 'healthy' | 'neutral'

interface SummaryMetric {
  label: string
  value: string
}

interface SummaryCardProps {
  title: string
  summary: string
  tone: Tone
  icon: LucideIcon
  detail: string
  metrics: SummaryMetric[]
}

interface IncidentGroup {
  key: string
  title: string
  detail: string
  count: number
  lastSeen?: string
  tone: Tone
}

function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ')
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exp)
  return `${value.toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`
}

function formatTimestamp(value?: string): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

function formatAge(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—'
  if (seconds < 1) return '<1s'
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86400)}d`
}

function formatPercent(value: number, digits = 0): string {
  return `${value.toFixed(digits)}%`
}

function formatRatioPercent(value: number): string {
  return `${(value * 100).toFixed(value * 100 >= 10 ? 0 : 1)}%`
}

function parseCloseCode(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function isAbnormalCloseCode(value: number | string | null | undefined): boolean {
  const code = parseCloseCode(value)
  return code !== null && code !== 1000
}

function computeGeneratedAgeSeconds(snapshot: SystemObservabilitySnapshot): number | undefined {
  if (typeof snapshot.collection.generated_age_seconds === 'number') {
    return snapshot.collection.generated_age_seconds
  }

  const generatedAt = new Date(snapshot.generated_at)
  if (Number.isNaN(generatedAt.getTime())) return undefined
  return Math.max(0, (Date.now() - generatedAt.getTime()) / 1000)
}

function getFreshnessSummary(snapshot: SystemObservabilitySnapshot): SummaryCardProps {
  const generatedAgeSeconds = computeGeneratedAgeSeconds(snapshot)
  const cacheAgeSeconds = snapshot.collection.cache_age_seconds
  const staleThresholdSeconds = snapshot.collection.stale_threshold_seconds
    ?? Math.max(snapshot.collection.cache_ttl_seconds * 2, 60)
  const freshnessState = snapshot.collection.freshness_state?.toLowerCase()
  const freshnessStateOverride = freshnessState === 'stale'
    ? true
    : freshnessState === 'fresh' || freshnessState === 'aging'
      ? false
      : undefined
  const explicitStale = snapshot.collection.is_stale
  const hasRefreshError = Boolean(snapshot.collection.last_refresh_error)
  const isStale = explicitStale
    ?? freshnessStateOverride
    ?? ((generatedAgeSeconds ?? cacheAgeSeconds) > staleThresholdSeconds)
  const isAging = !isStale && (generatedAgeSeconds ?? cacheAgeSeconds) > snapshot.collection.cache_ttl_seconds

  let tone: Tone = 'healthy'
  let title = 'Snapshot is fresh'
  if (hasRefreshError || isStale) {
    tone = 'critical'
    title = hasRefreshError ? 'Refresh degraded' : 'Snapshot is stale'
  } else if (isAging || freshnessState === 'aging') {
    tone = 'warning'
    title = 'Snapshot is aging'
  }

  const workerCount = snapshot.collection.worker_count
  const metrics: SummaryMetric[] = [
    { label: 'Generated', value: formatAge(generatedAgeSeconds) },
    { label: 'Cache age', value: formatAge(cacheAgeSeconds) },
    { label: 'Refresh', value: `${snapshot.collection.last_refresh_ms.toFixed(0)} ms` },
  ]
  if (typeof workerCount === 'number') {
    metrics.push({ label: 'Workers', value: String(workerCount) })
  }

  const detailParts = [
    `TTL ${formatAge(snapshot.collection.cache_ttl_seconds)}`,
    `stale threshold ${formatAge(staleThresholdSeconds)}`,
  ]
  if (snapshot.collection.last_refresh_error) {
    detailParts.push(`last error: ${snapshot.collection.last_refresh_error}`)
  }

  return {
    title,
    summary: `Generated ${formatAge(generatedAgeSeconds)} ago and cached for ${formatAge(cacheAgeSeconds)}.`,
    tone,
    icon: Clock3,
    detail: detailParts.join(' · '),
    metrics,
  }
}

function getSamplingSummary(snapshot: SystemObservabilitySnapshot): SummaryCardProps {
  const resourceCollection = snapshot.containers.resource_collection
  const runningContainers = snapshot.containers.running
  const coverageRatio = typeof resourceCollection.sampling_coverage_ratio === 'number'
    ? resourceCollection.sampling_coverage_ratio
    : runningContainers > 0
      ? resourceCollection.sampled_running_containers / runningContainers
      : 1
  const liveRunning = resourceCollection.live_running_containers ?? resourceCollection.sampled_running_containers
  const staleRunning = resourceCollection.stale_running_containers ?? 0
  const missingRunning = resourceCollection.missing_running_containers
    ?? Math.max(0, runningContainers - resourceCollection.sampled_running_containers)
  const scope = resourceCollection.resource_totals_scope
    ?? (coverageRatio >= 0.999 ? 'all_running_live' : 'partial_running')

  let tone: Tone = 'healthy'
  let title = 'Resource stats cover running containers'
  if (runningContainers === 0) {
    tone = 'neutral'
    title = 'No running containers to inspect'
  } else if (coverageRatio < 0.5) {
    tone = 'critical'
    title = 'Resource stats are partial'
  } else if (coverageRatio < 0.8 || missingRunning > 0 || resourceCollection.failed_running_containers > 0) {
    tone = 'warning'
    title = 'Resource coverage is degraded'
  } else if (staleRunning > 0) {
    tone = 'warning'
    title = 'Using cached resource stats for some containers'
  }

  return {
    title,
    summary: runningContainers === 0
      ? 'Totals are empty because no containers are currently running.'
      : `Stats available for ${resourceCollection.sampled_running_containers} of ${runningContainers} running containers (${formatRatioPercent(coverageRatio)}).`,
    tone,
    icon: Gauge,
    detail: `Scope ${scope} · live ${liveRunning} · cached ${staleRunning} · missing ${missingRunning} · failed live reads ${resourceCollection.failed_running_containers}`,
    metrics: [
      { label: 'Coverage', value: formatRatioPercent(coverageRatio) },
      { label: 'Available', value: `${resourceCollection.sampled_running_containers}/${runningContainers}` },
      { label: 'Live', value: String(liveRunning) },
      { label: 'Cached', value: String(staleRunning) },
      { label: 'Failed', value: String(resourceCollection.failed_running_containers) },
    ],
  }
}

function getTransportSummary(
  snapshot: SystemObservabilitySnapshot,
  incidentCount: number
): SummaryCardProps {
  const totalIssues = snapshot.system_health.websocket_connect_failures_total
    + snapshot.system_health.http_proxy_timeout_errors_total
    + snapshot.system_health.http_proxy_transport_errors_total

  let tone: Tone = 'healthy'
  let title = 'Transport path looks stable'
  if (snapshot.system_health.websocket_connect_failures_total > 0 || snapshot.system_health.http_proxy_timeout_errors_total > 0) {
    tone = 'critical'
    title = 'Transport issues detected'
  } else if (snapshot.system_health.http_proxy_transport_errors_total > 0 || incidentCount > 0) {
    tone = 'warning'
    title = 'Transport needs attention'
  }

  return {
    title,
    summary: `${snapshot.websocket.active_connections} active websocket sessions · HTTP p95 ${snapshot.http_proxy.latency_ms.p95.toFixed(0)} ms.`,
    tone,
    icon: Wifi,
    detail: `WS connect failures ${snapshot.system_health.websocket_connect_failures_total} · proxy timeouts ${snapshot.system_health.http_proxy_timeout_errors_total} · transport errors ${snapshot.system_health.http_proxy_transport_errors_total} · max latency ${snapshot.http_proxy.latency_ms.max.toFixed(0)} ms`,
    metrics: [
      { label: 'WS active', value: String(snapshot.websocket.active_connections) },
      { label: 'WS failures', value: String(snapshot.system_health.websocket_connect_failures_total) },
      { label: 'HTTP p95', value: `${snapshot.http_proxy.latency_ms.p95.toFixed(0)} ms` },
      { label: 'Incidents', value: String(totalIssues || incidentCount) },
    ],
  }
}

function getClientSummary(
  snapshot: SystemObservabilitySnapshot,
  incidentEvents: ClientObservabilityEvent[]
): SummaryCardProps {
  const impactedApps = new Set(
    incidentEvents
      .map((event) => event.app_name?.trim())
      .filter((value): value is string => Boolean(value))
  )

  let tone: Tone = 'healthy'
  let title = 'Client telemetry is quiet'
  if (snapshot.system_health.client_unhandled_errors_total > 0 || snapshot.system_health.client_terminal_ws_errors_total > 0) {
    tone = 'critical'
    title = 'Client-facing failures detected'
  } else if (snapshot.system_health.client_error_events_total > 0 || incidentEvents.length > 0) {
    tone = 'warning'
    title = 'Client telemetry needs review'
  }

  return {
    title,
    summary: `${snapshot.system_health.client_error_events_total} client error events since the current telemetry window started.`,
    tone,
    icon: AlertTriangle,
    detail: `Terminal WS errors ${snapshot.system_health.client_terminal_ws_errors_total} · recoveries ${snapshot.system_health.client_terminal_recovery_events_total} · iframe errors ${snapshot.system_health.client_iframe_load_errors_total} · browser start failures ${snapshot.system_health.client_browser_service_start_failures_total}`,
    metrics: [
      { label: 'Recent incidents', value: String(incidentEvents.length) },
      { label: 'Unhandled', value: String(snapshot.system_health.client_unhandled_errors_total) },
      { label: 'Recoveries', value: String(snapshot.system_health.client_terminal_recovery_events_total) },
      { label: 'Impacted apps', value: String(impactedApps.size) },
    ],
  }
}

function topEntries(entries: Record<string, number>, limit = 4): Array<[string, number]> {
  return Object.entries(entries)
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
}

function containerRiskScore(item: SystemObservabilityContainer): number {
  let score = 0
  if (item.health === 'unhealthy') score += 100
  if (item.status !== 'running') score += 20
  if (item.status === 'dead' || item.status === 'restarting' || item.status === 'paused') score += 30
  if (item.status === 'exited') score += 10
  if (!item.metrics_sampled && item.status === 'running') score += 15
  score += Math.min(item.restart_count, 20)
  return score
}

function isWebsocketIncident(event: WebSocketObservabilityEvent): boolean {
  if (event.event === 'connect_failure') return true
  if (event.event !== 'closed') return false
  return event.initiator === 'proxy_error'
    || event.initiator === 'backend'
    || isAbnormalCloseCode(event.client_close_code)
    || isAbnormalCloseCode(event.backend_close_code)
}

function isClientIncident(event: ClientObservabilityEvent): boolean {
  if (event.event_type === 'terminal_ws_closed' || event.event_type === 'terminal_ws_buffer_stall') {
    return false
  }
  return event.severity === 'error' || event.severity === 'warning'
}

function describeWebsocketEvent(event: WebSocketObservabilityEvent): { title: string; detail: string; tone: Tone } {
  const target = event.target_host ? `${event.target_host}:${String(event.target_port ?? '—')}` : 'unknown target'
  if (event.event === 'connect_failure') {
    return {
      title: event.reason || 'connect failure',
      detail: `${event.path || '—'} -> ${target}${event.error ? ` · ${event.error}` : ''}`,
      tone: 'critical',
    }
  }

  const clientCode = parseCloseCode(event.client_close_code)
  const backendCode = parseCloseCode(event.backend_close_code)
  const codeSummary = [
    clientCode !== null ? `client ${clientCode}` : null,
    backendCode !== null ? `backend ${backendCode}` : null,
  ].filter(Boolean).join(' / ')

  return {
    title: event.initiator ? `closed by ${event.initiator}` : 'closed',
    detail: `${event.path || '—'} -> ${target}${codeSummary ? ` · ${codeSummary}` : ''}`,
    tone: isAbnormalCloseCode(event.client_close_code) || isAbnormalCloseCode(event.backend_close_code) ? 'warning' : 'neutral',
  }
}

function describeClientEvent(event: ClientObservabilityEvent): { title: string; detail: string; tone: Tone } {
  const location = event.app_name || event.panel || 'unknown target'
  const detailBits = [event.message, event.path].filter(Boolean)
  return {
    title: `${event.event_type} (${location})`,
    detail: detailBits.join(' · ') || 'No extra context captured',
    tone: event.severity === 'error' ? 'critical' : event.severity === 'warning' ? 'warning' : 'neutral',
  }
}

function buildWebsocketIncidentGroups(events: WebSocketObservabilityEvent[]): IncidentGroup[] {
  const groups = new Map<string, IncidentGroup & { sortTime: number }>()

  for (const event of events) {
    const description = describeWebsocketEvent(event)
    const key = [
      event.event,
      event.path || '',
      event.target_host || '',
      event.reason || '',
      event.initiator || '',
      String(parseCloseCode(event.client_close_code) ?? ''),
      String(parseCloseCode(event.backend_close_code) ?? ''),
    ].join('|')
    const sortTime = Date.parse(event.timestamp || '') || 0
    const existing = groups.get(key)
    if (existing) {
      existing.count += 1
      if (sortTime >= existing.sortTime) {
        existing.lastSeen = event.timestamp
        existing.detail = description.detail
        existing.tone = description.tone
        existing.sortTime = sortTime
      }
      continue
    }

    groups.set(key, {
      key,
      title: description.title,
      detail: description.detail,
      count: 1,
      lastSeen: event.timestamp,
      tone: description.tone,
      sortTime,
    })
  }

  return Array.from(groups.values())
    .sort((a, b) => b.count - a.count || b.sortTime - a.sortTime)
    .slice(0, 8)
}

function buildClientIncidentGroups(events: ClientObservabilityEvent[]): IncidentGroup[] {
  const groups = new Map<string, IncidentGroup & { sortTime: number }>()

  for (const event of events) {
    const description = describeClientEvent(event)
    const key = [
      event.app_name || '',
      event.panel || '',
      event.event_type || '',
      event.message || '',
      event.severity || '',
    ].join('|')
    const sortTime = Date.parse(event.timestamp || '') || 0
    const existing = groups.get(key)
    if (existing) {
      existing.count += 1
      if (sortTime >= existing.sortTime) {
        existing.lastSeen = event.timestamp
        existing.detail = description.detail
        existing.tone = description.tone
        existing.sortTime = sortTime
      }
      continue
    }

    groups.set(key, {
      key,
      title: description.title,
      detail: description.detail,
      count: 1,
      lastSeen: event.timestamp,
      tone: description.tone,
      sortTime,
    })
  }

  return Array.from(groups.values())
    .sort((a, b) => b.count - a.count || b.sortTime - a.sortTime)
    .slice(0, 8)
}

function getAlertTone(alert: OpsAlertRecord): Tone {
  if (!alert.active) return 'neutral'
  if (alert.severity === 'critical') return 'critical'
  if (alert.severity === 'warning') return 'warning'
  return 'healthy'
}

function getAlertTitle(alert: OpsAlertRecord): string {
  if (alert.title?.trim()) return alert.title
  const containerName = typeof alert.dimensions.container_name === 'string' ? alert.dimensions.container_name : null
  const appName = typeof alert.dimensions.app_name === 'string' ? alert.dimensions.app_name : null
  if (containerName && alert.alert_key.startsWith('container_unhealthy:')) return `${containerName} unhealthy`
  if (containerName && alert.alert_key.startsWith('container_not_running:')) return `${containerName} not running`
  if (containerName && alert.alert_key.startsWith('container_metrics_missing:')) return `${containerName} missing resource stats`
  if (containerName && alert.alert_key.startsWith('container_metrics_cached:')) return `${containerName} using cached resource stats`
  if (appName) return `${appName} alert`
  return alert.alert_key.replace(/_/g, ' ')
}

function getAlertDetail(alert: OpsAlertRecord): string {
  const detailBits = [
    typeof alert.dimensions.kind === 'string' ? `kind ${alert.dimensions.kind}` : null,
    typeof alert.dimensions.container_name === 'string' ? `container ${alert.dimensions.container_name}` : null,
    typeof alert.dimensions.app_name === 'string' ? `app ${alert.dimensions.app_name}` : null,
    typeof alert.dimensions.user_email === 'string' ? `user ${alert.dimensions.user_email}` : null,
  ].filter(Boolean)
  return detailBits.join(' · ') || 'No extra alert dimensions recorded'
}

function matchesContainerAlert(container: SystemObservabilityContainer, alert: OpsAlertRecord): boolean {
  const containerName = typeof alert.dimensions.container_name === 'string' ? alert.dimensions.container_name.toLowerCase() : null
  const appName = typeof alert.dimensions.app_name === 'string' ? alert.dimensions.app_name.toLowerCase() : null
  const userEmail = typeof alert.dimensions.user_email === 'string' ? alert.dimensions.user_email.toLowerCase() : null
  return containerName === container.name.toLowerCase()
    || Boolean(appName && container.labels.app_name?.toLowerCase() === appName)
    || Boolean(userEmail && container.labels.user_email?.toLowerCase() === userEmail)
}

function buildContainerHistoryChart(metrics: OpsMetricSnapshot[]): Array<Record<string, number | string>> {
  const points = new Map<string, Record<string, number | string>>()
  const sorted = [...metrics].sort(
    (left, right) => new Date(left.computed_at).getTime() - new Date(right.computed_at).getTime()
  )

  for (const metric of sorted) {
    const timestamp = metric.computed_at
    const point = points.get(timestamp) ?? {
      date: new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestamp,
    }
    switch (metric.metric_name) {
      case 'ops_container_cpu_percent':
        point.cpu_percent = metric.metric_value
        break
      case 'ops_container_memory_percent':
        point.memory_percent = metric.metric_value
        break
      case 'ops_container_restart_count':
        point.restart_count = metric.metric_value
        break
      case 'ops_container_stats_age_seconds':
        point.stats_age_seconds = metric.metric_value
        break
      case 'ops_container_stats_available':
        point.stats_available = metric.metric_value
        break
      case 'ops_container_stats_fresh':
        point.stats_fresh = metric.metric_value
        break
      case 'ops_container_health_unhealthy':
        point.health_unhealthy = metric.metric_value
        break
      case 'ops_container_status_running':
        point.status_running = metric.metric_value
        break
      default:
        break
    }
    points.set(timestamp, point)
  }

  return Array.from(points.values())
}

function matchesContainerWebsocketEvent(
  container: SystemObservabilityContainer,
  event: WebSocketObservabilityEvent
): boolean {
  const containerName = container.name.toLowerCase()
  const targetHost = event.target_host?.toLowerCase()
  const path = event.path?.toLowerCase() || ''
  const appName = container.labels.app_name?.toLowerCase()
  const userEmail = container.labels.user_email?.toLowerCase()

  if (targetHost && (targetHost === containerName || targetHost.includes(containerName))) {
    return true
  }
  if (appName && (path.includes(`/${appName}/`) || path.includes(appName) || targetHost?.includes(appName))) {
    return true
  }
  return Boolean(userEmail && path.includes(userEmail))
}

function matchesContainerClientEvent(
  container: SystemObservabilityContainer,
  event: ClientObservabilityEvent
): boolean {
  const appName = container.labels.app_name?.toLowerCase()
  const userEmail = container.labels.user_email?.toLowerCase()
  const eventApp = event.app_name?.toLowerCase()
  const eventUser = event.user_email?.toLowerCase()
  const path = event.path?.toLowerCase() || ''

  if (appName && (eventApp === appName || path.includes(`/${appName}/`) || path.includes(appName))) {
    return true
  }
  return Boolean(userEmail && (eventUser === userEmail || path.includes(userEmail)))
}

function tonePanelClasses(tone: Tone): string {
  if (tone === 'critical') return 'border-red-500/30 bg-red-500/5'
  if (tone === 'warning') return 'border-amber-500/30 bg-amber-500/5'
  if (tone === 'healthy') return 'border-emerald-500/30 bg-emerald-500/5'
  return 'border-border bg-card'
}

function toneBadgeClasses(tone: Tone): string {
  if (tone === 'critical') return 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-300'
  if (tone === 'warning') return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
  if (tone === 'healthy') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
  return 'border-border bg-secondary text-secondary-foreground'
}

function SummaryCard({ title, summary, tone, icon: Icon, detail, metrics }: SummaryCardProps) {
  return (
    <div className={cn('rounded-2xl border p-5', tonePanelClasses(tone))}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Ops signal</div>
          <h3 className="mt-2 text-lg font-semibold">{title}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{summary}</p>
        </div>
        <div className={cn('rounded-xl border p-2', toneBadgeClasses(tone))}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {metrics.map((metric) => (
          <div key={metric.label} className="rounded-xl border border-border/60 bg-background/70 p-3">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{metric.label}</div>
            <div className="mt-1 text-lg font-semibold">{metric.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 text-xs text-muted-foreground">{detail}</div>
    </div>
  )
}

function Badge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium', toneBadgeClasses(tone))}>
      {children}
    </span>
  )
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  )
}

function HotspotList({
  emptyText,
  groups,
}: {
  emptyText: string
  groups: IncidentGroup[]
}) {
  if (groups.length === 0) {
    return <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">{emptyText}</div>
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.key} className="rounded-xl border border-border bg-background/60 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge tone={group.tone}>{group.count}x</Badge>
                <span className="font-medium">{group.title}</span>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">{group.detail}</div>
            </div>
            <div className="shrink-0 text-xs text-muted-foreground">{formatTimestamp(group.lastSeen)}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function SystemObservability() {
  const [snapshot, setSnapshot] = useState<SystemObservabilitySnapshot | null>(null)
  const [durableAlerts, setDurableAlerts] = useState<OpsAlertRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [alertsError, setAlertsError] = useState<string | null>(null)
  const [includeStopped, setIncludeStopped] = useState(false)
  const [kindFilter, setKindFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null)
  const [containerHistory, setContainerHistory] = useState<OpsMetricSnapshot[]>([])
  const [containerHistoryLoading, setContainerHistoryLoading] = useState(false)
  const [containerHistoryError, setContainerHistoryError] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    try {
      const [observabilityResult, alertsResult] = await Promise.allSettled([
        getSystemObservability({
          include_stopped: includeStopped,
          limit: 400,
          history_points: 180,
        }),
        getOpsAlerts({ limit: 200 }),
      ])

      if (observabilityResult.status === 'rejected') {
        throw observabilityResult.reason
      }

      setSnapshot(observabilityResult.value)
      setError(null)

      if (alertsResult.status === 'fulfilled') {
        setDurableAlerts(alertsResult.value)
        setAlertsError(null)
      } else {
        setDurableAlerts([])
        setAlertsError(alertsResult.reason instanceof Error ? alertsResult.reason.message : 'Failed to load durable alerts')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load observability data')
      throw err
    } finally {
      setLoading(false)
    }
  }, [includeStopped])

  useEffect(() => {
    setLoading(true)
    loadData()
  }, [loadData])

  const refreshState = useAutoRefresh(loadData, {
    baseInterval: 30000,
    maxInterval: 120000,
  })

  const chartData = useMemo(() => {
    if (!snapshot) return []
    let prevFailures = 0
    let prevClosed = 0
    let prevHttpTransportErrors = 0
    let prevHttpTimeoutErrors = 0
    let prevClientErrors = 0
    let prevClientTerminalWsErrors = 0
    let prevClientTerminalRecoveryEvents = 0
    let prevClientUnhandledErrors = 0
    return snapshot.history.map((point) => {
      const failureDelta = Math.max(0, point.websocket_connect_failures_total - prevFailures)
      const closedDelta = Math.max(0, point.websocket_closed_connections - prevClosed)
      const httpTransportErrorDelta = Math.max(0, point.http_proxy_transport_errors_total - prevHttpTransportErrors)
      const httpTimeoutErrorDelta = Math.max(0, point.http_proxy_timeout_errors_total - prevHttpTimeoutErrors)
      const clientErrorDelta = Math.max(0, point.client_error_events_total - prevClientErrors)
      const clientTerminalWsErrorDelta = Math.max(0, point.client_terminal_ws_errors_total - prevClientTerminalWsErrors)
      const clientTerminalRecoveryDelta = Math.max(0, point.client_terminal_recovery_events_total - prevClientTerminalRecoveryEvents)
      const clientUnhandledErrorDelta = Math.max(0, point.client_unhandled_errors_total - prevClientUnhandledErrors)
      prevFailures = point.websocket_connect_failures_total
      prevClosed = point.websocket_closed_connections
      prevHttpTransportErrors = point.http_proxy_transport_errors_total
      prevHttpTimeoutErrors = point.http_proxy_timeout_errors_total
      prevClientErrors = point.client_error_events_total
      prevClientTerminalWsErrors = point.client_terminal_ws_errors_total
      prevClientTerminalRecoveryEvents = point.client_terminal_recovery_events_total
      prevClientUnhandledErrors = point.client_unhandled_errors_total
      return {
        date: new Date(point.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        running_containers: point.running_containers,
        unhealthy_containers: point.unhealthy_containers,
        cpu_percent_sum: point.cpu_percent_sum,
        memory_percent: point.memory_percent,
        websocket_active: point.websocket_active,
        websocket_failures_delta: failureDelta,
        websocket_closed_delta: closedDelta,
        http_proxy_p50_latency_ms: point.http_proxy_p50_latency_ms,
        http_proxy_p95_latency_ms: point.http_proxy_p95_latency_ms,
        http_proxy_transport_errors_delta: httpTransportErrorDelta,
        http_proxy_timeout_errors_delta: httpTimeoutErrorDelta,
        client_error_events_delta: clientErrorDelta,
        client_terminal_ws_errors_delta: clientTerminalWsErrorDelta,
        client_terminal_recovery_events_delta: clientTerminalRecoveryDelta,
        client_unhandled_errors_delta: clientUnhandledErrorDelta,
      }
    })
  }, [snapshot])

  const rows = useMemo(() => {
    if (!snapshot) return []
    const q = query.trim().toLowerCase()
    return [...snapshot.containers.items]
      .filter((item) => {
        if (kindFilter !== 'all' && item.kind !== kindFilter) return false
        if (statusFilter !== 'all' && item.status !== statusFilter) return false
        if (!q) return true
        const haystack = [
          item.name,
          item.kind,
          item.status,
          item.health,
          item.labels.app_name ?? '',
          item.labels.user_email ?? '',
          item.labels.deployment ?? '',
          item.labels.platform ?? '',
        ]
          .join(' ')
          .toLowerCase()
        return haystack.includes(q)
      })
      .sort((a, b) => containerRiskScore(b) - containerRiskScore(a) || b.restart_count - a.restart_count || a.name.localeCompare(b.name))
  }, [snapshot, kindFilter, statusFilter, query])

  useEffect(() => {
    if (rows.length === 0) {
      setSelectedContainerId(null)
      return
    }
    if (!selectedContainerId || !rows.some((item) => item.id === selectedContainerId)) {
      setSelectedContainerId(rows[0].id)
    }
  }, [rows, selectedContainerId])

  const selectedContainer = useMemo(
    () => rows.find((item) => item.id === selectedContainerId) ?? null,
    [rows, selectedContainerId]
  )

  const activeDurableAlerts = useMemo(
    () => durableAlerts.filter((alert) => alert.active),
    [durableAlerts]
  )

  useEffect(() => {
    if (!selectedContainer) {
      setContainerHistory([])
      setContainerHistoryError(null)
      setContainerHistoryLoading(false)
      return
    }

    let cancelled = false
    setContainerHistoryLoading(true)
    getOpsContainerHistory({
      container_name: selectedContainer.name,
      app_name: selectedContainer.labels.app_name || undefined,
      kind: selectedContainer.kind,
      since_minutes: 360,
      limit: 720,
    })
      .then((data) => {
        if (cancelled) return
        setContainerHistory(data)
        setContainerHistoryError(null)
      })
      .catch((err) => {
        if (cancelled) return
        setContainerHistory([])
        setContainerHistoryError(err instanceof Error ? err.message : 'Failed to load container history')
      })
      .finally(() => {
        if (!cancelled) {
          setContainerHistoryLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [selectedContainer, snapshot?.generated_at])

  const coverageRatio = useMemo(() => {
    if (!snapshot) return 1
    const providedRatio = snapshot.containers.resource_collection.sampling_coverage_ratio
    if (typeof providedRatio === 'number') return providedRatio
    return snapshot.containers.running > 0
      ? snapshot.containers.resource_collection.sampled_running_containers / snapshot.containers.running
      : 1
  }, [snapshot])

  const wsRecentEvents = useMemo(() => {
    if (!snapshot) return []
    return [...snapshot.websocket.recent_events].reverse().slice(0, 40)
  }, [snapshot])

  const clientRecentEvents = useMemo(() => {
    if (!snapshot) return []
    return [...snapshot.client.recent_events].reverse().slice(0, 40)
  }, [snapshot])

  const wsIncidentEvents = useMemo(
    () => wsRecentEvents.filter(isWebsocketIncident),
    [wsRecentEvents]
  )

  const clientIncidentEvents = useMemo(
    () => clientRecentEvents.filter(isClientIncident),
    [clientRecentEvents]
  )

  const websocketGroups = useMemo(
    () => buildWebsocketIncidentGroups(wsIncidentEvents),
    [wsIncidentEvents]
  )

  const clientGroups = useMemo(
    () => buildClientIncidentGroups(clientIncidentEvents),
    [clientIncidentEvents]
  )

  const selectedContainerWebsocketEvents = useMemo(() => {
    if (!selectedContainer) return []
    return wsIncidentEvents.filter((event) => matchesContainerWebsocketEvent(selectedContainer, event)).slice(0, 8)
  }, [selectedContainer, wsIncidentEvents])

  const selectedContainerClientEvents = useMemo(() => {
    if (!selectedContainer) return []
    return clientIncidentEvents.filter((event) => matchesContainerClientEvent(selectedContainer, event)).slice(0, 8)
  }, [selectedContainer, clientIncidentEvents])

  const selectedContainerDurableAlerts = useMemo(() => {
    if (!selectedContainer) return []
    return activeDurableAlerts.filter((alert) => matchesContainerAlert(selectedContainer, alert)).slice(0, 8)
  }, [activeDurableAlerts, selectedContainer])

  const containerHistoryChart = useMemo(
    () => buildContainerHistoryChart(containerHistory),
    [containerHistory]
  )

  const latestContainerHistoryPoint = useMemo(
    () => (containerHistoryChart.length > 0 ? containerHistoryChart[containerHistoryChart.length - 1] : null),
    [containerHistoryChart]
  )

  const freshnessSummary = useMemo(
    () => (snapshot ? getFreshnessSummary(snapshot) : null),
    [snapshot]
  )
  const samplingSummary = useMemo(
    () => (snapshot ? getSamplingSummary(snapshot) : null),
    [snapshot]
  )
  const transportSummary = useMemo(
    () => (snapshot ? getTransportSummary(snapshot, websocketGroups.length) : null),
    [snapshot, websocketGroups.length]
  )
  const clientSummary = useMemo(
    () => (snapshot ? getClientSummary(snapshot, clientIncidentEvents) : null),
    [snapshot, clientIncidentEvents]
  )

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="mb-2 text-2xl font-bold">System Observability</h1>
          <p className="text-muted-foreground">Live container, websocket, proxy, and browser telemetry with operator drill-through.</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      <AnalyticsNav />

      {error && (
        <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
          {error}
        </div>
      )}

      <AutoRefreshNotice
        errorMessage={refreshState.lastErrorMessage}
        currentIntervalMs={refreshState.currentInterval}
        retryAfterSeconds={refreshState.retryAfterSeconds}
        isPaused={refreshState.isPaused}
      />

      {loading && !snapshot ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !snapshot ? (
        <div className="py-12 text-center text-muted-foreground">No observability data available</div>
      ) : (
        <>
          <div className="mb-3 text-xs text-muted-foreground">
            Snapshot: {formatTimestamp(snapshot.generated_at)}
            {' · '}
            Cache age: {snapshot.collection.cache_age_seconds.toFixed(1)}s
            {' · '}
            Refresh time: {snapshot.collection.last_refresh_ms.toFixed(0)}ms
          </div>

          <div className="mb-8 grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-4">
            {freshnessSummary && <SummaryCard {...freshnessSummary} />}
            {samplingSummary && <SummaryCard {...samplingSummary} />}
            {transportSummary && <SummaryCard {...transportSummary} />}
            {clientSummary && <SummaryCard {...clientSummary} />}
          </div>

          <div className="mb-8 rounded-xl border border-border bg-card p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Active durable alerts</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Backed by persisted metric snapshots rather than the router’s in-memory telemetry window.
                </p>
              </div>
              <Badge tone={activeDurableAlerts.length > 0 ? 'critical' : 'healthy'}>
                {activeDurableAlerts.length} active
              </Badge>
            </div>
            {alertsError ? (
              <div className="mt-4 rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
                {alertsError}
              </div>
            ) : activeDurableAlerts.length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                No active durable alerts are currently recorded.
              </div>
            ) : (
              <div className="mt-4 grid gap-3 xl:grid-cols-2 2xl:grid-cols-3">
                {activeDurableAlerts.slice(0, 12).map((alert) => (
                  <div key={alert.alert_key} className={cn('rounded-xl border p-4', tonePanelClasses(getAlertTone(alert)))}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge tone={getAlertTone(alert)}>{alert.severity}</Badge>
                          <span className="text-xs text-muted-foreground">{formatTimestamp(alert.computed_at)}</span>
                        </div>
                        <div className="mt-2 font-medium">{getAlertTitle(alert)}</div>
                      </div>
                      <div className="rounded-lg border border-border/70 bg-background/60 px-2 py-1 text-sm font-semibold">
                        {alert.metric_value.toFixed(alert.metric_value >= 10 ? 0 : 1)}
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-muted-foreground">{getAlertDetail(alert)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Unhealthy Containers" value={snapshot.containers.unhealthy} icon={AlertTriangle} />
            <StatCard label="Running Containers" value={snapshot.containers.running} icon={Server} />
            <StatCard label="Stats Coverage" value={formatRatioPercent(coverageRatio)} icon={Gauge} />
            <StatCard label="WS Active" value={snapshot.websocket.active_connections} icon={Wifi} />
            <StatCard label="WS Connect Failures" value={snapshot.system_health.websocket_connect_failures_total} icon={Activity} />
            <StatCard label="HTTP p95" value={`${snapshot.http_proxy.latency_ms.p95.toFixed(0)} ms`} icon={Activity} />
            <StatCard label="Client Errors" value={snapshot.system_health.client_error_events_total} icon={AlertTriangle} />
            <StatCard label="Unhandled Client Errors" value={snapshot.system_health.client_unhandled_errors_total} icon={AlertTriangle} />
          </div>

          <div className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2 2xl:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Websocket Trend</h3>
              <TrendChart
                data={chartData}
                lines={[
                  { dataKey: 'websocket_active', label: 'WS Active', color: CHART_PALETTE[0] },
                  { dataKey: 'websocket_failures_delta', label: 'WS Failures/Sample', color: CHART_PALETTE[3] },
                  { dataKey: 'websocket_closed_delta', label: 'WS Closes/Sample', color: CHART_PALETTE[2] },
                ]}
                height={280}
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Resource Trend</h3>
              <TrendChart
                data={chartData}
                lines={[
                  { dataKey: 'running_containers', label: 'Running Containers', color: CHART_PALETTE[1] },
                  { dataKey: 'memory_percent', label: 'Memory %', color: CHART_PALETTE[4] },
                  { dataKey: 'cpu_percent_sum', label: 'CPU % Sum', color: CHART_PALETTE[5] },
                ]}
                height={280}
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Responsiveness Trend</h3>
              <TrendChart
                data={chartData}
                lines={[
                  { dataKey: 'http_proxy_p50_latency_ms', label: 'HTTP p50 (ms)', color: CHART_PALETTE[5] },
                  { dataKey: 'http_proxy_p95_latency_ms', label: 'HTTP p95 (ms)', color: CHART_PALETTE[4] },
                  { dataKey: 'http_proxy_timeout_errors_delta', label: 'Timeouts/Sample', color: CHART_PALETTE[3] },
                  { dataKey: 'http_proxy_transport_errors_delta', label: 'Transport Errors/Sample', color: CHART_PALETTE[2] },
                ]}
                height={280}
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Client Failure Trend</h3>
              <TrendChart
                data={chartData}
                lines={[
                  { dataKey: 'client_error_events_delta', label: 'Client Errors/Sample', color: CHART_PALETTE[3] },
                  { dataKey: 'client_terminal_ws_errors_delta', label: 'Terminal WS Errors/Sample', color: CHART_PALETTE[0] },
                  { dataKey: 'client_unhandled_errors_delta', label: 'Unhandled Errors/Sample', color: CHART_PALETTE[2] },
                  { dataKey: 'client_terminal_recovery_events_delta', label: 'Recoveries/Sample', color: CHART_PALETTE[5] },
                ]}
                height={280}
              />
            </div>
          </div>

          <div className="mb-6 grid gap-6 xl:grid-cols-[minmax(0,1.65fr)_minmax(340px,1fr)]">
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={includeStopped}
                      onChange={(event) => setIncludeStopped(event.target.checked)}
                      className="rounded border-border"
                    />
                    Include stopped
                  </label>
                  <select
                    value={kindFilter}
                    onChange={(event) => setKindFilter(event.target.value)}
                    className="rounded border border-border bg-background px-3 py-1.5 text-sm"
                  >
                    <option value="all">All kinds</option>
                    <option value="workspace">Workspace</option>
                    <option value="app">App</option>
                    <option value="platform">Platform</option>
                    <option value="other">Other</option>
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="rounded border border-border bg-background px-3 py-1.5 text-sm"
                  >
                    <option value="all">All statuses</option>
                    {Object.keys(snapshot.containers.status_counts).map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Filter by container, app, user, deployment..."
                    className="min-w-[240px] rounded border border-border bg-background px-3 py-1.5 text-sm"
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span>{rows.length} of {snapshot.containers.items.length} containers shown</span>
                  <span>·</span>
                  <span>{snapshot.containers.unhealthy} unhealthy</span>
                  <span>·</span>
                  <span>{snapshot.containers.returned} returned{snapshot.containers.truncated ? ' (truncated by API limit)' : ''}</span>
                  <span>·</span>
                  <span>Resource totals: CPU {formatPercent(snapshot.containers.resource_totals.cpu_percent_sum, 1)} · memory {formatPercent(snapshot.containers.resource_totals.memory_percent, 1)}</span>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-border bg-card">
                <table className="w-full min-w-[1260px]">
                  <thead className="bg-secondary">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">Container</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Scope</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Resources</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Signals</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((item) => {
                      const isSelected = item.id === selectedContainerId
                      const rowTone: Tone = item.health === 'unhealthy'
                        ? 'critical'
                        : item.status !== 'running'
                          ? 'warning'
                          : !item.metrics_sampled
                            ? 'neutral'
                            : item.metrics_fresh === false
                              ? 'warning'
                              : 'healthy'
                      const relatedClientCount = clientIncidentEvents.filter((event) => matchesContainerClientEvent(item, event)).length
                      const relatedWsCount = wsIncidentEvents.filter((event) => matchesContainerWebsocketEvent(item, event)).length

                      return (
                        <tr
                          key={item.id}
                          className={cn(
                            'hover:bg-secondary/50',
                            isSelected && 'bg-secondary/70'
                          )}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-xs text-muted-foreground">Started: {formatTimestamp(item.started_at)}</div>
                            <div className="text-xs text-muted-foreground">Created: {formatTimestamp(item.created_at)}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="capitalize">{item.kind}</div>
                            <div className="mt-1 text-xs text-muted-foreground">app: {item.labels.app_name || '—'}</div>
                            <div className="text-xs text-muted-foreground">user: {item.labels.user_email || '—'}</div>
                            <div className="text-xs text-muted-foreground">deploy: {item.labels.deployment || '—'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              <Badge tone={rowTone}>{item.status}</Badge>
                              <Badge tone={item.health === 'unhealthy' ? 'critical' : item.health === 'healthy' ? 'healthy' : 'neutral'}>
                                health: {item.health}
                              </Badge>
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground">Restarts: {item.restart_count}</div>
                            {item.finished_at && (
                              <div className="text-xs text-muted-foreground">Finished: {formatTimestamp(item.finished_at)}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div>{item.metrics_sampled ? `${item.cpu_percent.toFixed(2)}% CPU` : 'CPU unavailable'}</div>
                            <div>{item.metrics_sampled ? `${item.memory_percent.toFixed(2)}% memory` : 'Memory unavailable'}</div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              {item.metrics_sampled
                                ? `${formatBytes(item.memory_usage_bytes)} / ${formatBytes(item.memory_limit_bytes)}`
                                : 'No resource stats available yet'}
                            </div>
                            {item.metrics_sampled && item.metrics_fresh === false && (
                              <div className="mt-1 text-xs text-amber-700">
                                Cached stats from {formatAge(item.stats_age_seconds)} ago
                              </div>
                            )}
                            <div className="mt-1 text-xs text-muted-foreground">
                              RX {item.metrics_sampled ? formatBytes(item.network_rx_bytes) : '—'} · TX {item.metrics_sampled ? formatBytes(item.network_tx_bytes) : '—'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="text-xs text-muted-foreground">WebSocket incidents: {relatedWsCount}</div>
                            <div className="text-xs text-muted-foreground">Client incidents: {relatedClientCount}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {!item.metrics_sampled && <Badge tone="warning">metrics missing</Badge>}
                              {item.metrics_sampled && item.metrics_fresh === false && <Badge tone="warning">cached metrics</Badge>}
                              {item.restart_count > 0 && <Badge tone={item.restart_count >= 3 ? 'critical' : 'warning'}>{item.restart_count} restarts</Badge>}
                              {item.health === 'unhealthy' && <Badge tone="critical">needs investigation</Badge>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col items-start gap-2">
                              <button
                                type="button"
                                onClick={() => setSelectedContainerId(item.id)}
                                className={cn(
                                  'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                                  isSelected
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-border hover:bg-secondary'
                                )}
                              >
                                {isSelected ? 'Selected' : 'Investigate'}
                              </button>
                              {item.labels.app_name && (
                                <Link
                                  to={getAdminAppsPath({ query: item.labels.app_name })}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                >
                                  Open admin app
                                  <ExternalLink className="h-3 w-3" />
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <aside className="self-start rounded-xl border border-border bg-card p-5 xl:sticky xl:top-6">
              {selectedContainer ? (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Selected container</div>
                      <h3 className="mt-2 text-xl font-semibold">{selectedContainer.name}</h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Badge tone={selectedContainer.health === 'unhealthy' ? 'critical' : selectedContainer.health === 'healthy' ? 'healthy' : 'neutral'}>
                          {selectedContainer.health}
                        </Badge>
                        <Badge tone={selectedContainer.status === 'running' ? 'healthy' : 'warning'}>
                          {selectedContainer.status}
                        </Badge>
                        <Badge
                          tone={
                            !selectedContainer.metrics_sampled
                              ? 'warning'
                              : selectedContainer.metrics_fresh === false
                                ? 'warning'
                                : 'healthy'
                          }
                        >
                          {!selectedContainer.metrics_sampled
                            ? 'metrics missing'
                            : selectedContainer.metrics_fresh === false
                              ? 'cached metrics'
                              : 'live metrics'}
                        </Badge>
                      </div>
                    </div>
                    <div className="rounded-xl border border-border bg-secondary/40 p-3 text-right">
                      <div className="text-xs text-muted-foreground">Restarts</div>
                      <div className="text-2xl font-semibold">{selectedContainer.restart_count}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {selectedContainer.labels.app_name && (
                      <>
                        <Link
                          to={getAdminAppsPath({ query: selectedContainer.labels.app_name })}
                          className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary"
                        >
                          Open admin app
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => setQuery(selectedContainer.labels.app_name || '')}
                          className="rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary"
                        >
                          Filter this app
                        </button>
                      </>
                    )}
                    {selectedContainer.labels.user_email && (
                      <button
                        type="button"
                        onClick={() => setQuery(selectedContainer.labels.user_email || '')}
                        className="rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary"
                      >
                        Filter this user
                      </button>
                    )}
                  </div>

                  <div className="mt-6 space-y-1 border-t border-border pt-4">
                    <DetailRow label="Kind" value={<span className="capitalize">{selectedContainer.kind}</span>} />
                    <DetailRow label="Started" value={formatTimestamp(selectedContainer.started_at)} />
                    <DetailRow label="Created" value={formatTimestamp(selectedContainer.created_at)} />
                    <DetailRow label="Finished" value={formatTimestamp(selectedContainer.finished_at)} />
                    <DetailRow label="App" value={selectedContainer.labels.app_name || '—'} />
                    <DetailRow label="User" value={selectedContainer.labels.user_email || '—'} />
                    <DetailRow label="Deployment" value={selectedContainer.labels.deployment || '—'} />
                    <DetailRow label="Platform" value={selectedContainer.labels.platform || '—'} />
                    <DetailRow
                      label="Stats source"
                      value={
                        !selectedContainer.metrics_sampled
                          ? 'missing'
                          : selectedContainer.metrics_fresh === false
                            ? `cached (${formatAge(selectedContainer.stats_age_seconds)} old)`
                            : 'live'
                      }
                    />
                  </div>

                  <div className="mt-6 rounded-xl border border-border bg-background/60 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="font-semibold">Current resource view</h4>
                      <Badge
                        tone={
                          !selectedContainer.metrics_sampled
                            ? 'warning'
                            : selectedContainer.metrics_fresh === false
                              ? 'warning'
                              : 'healthy'
                        }
                      >
                        {!selectedContainer.metrics_sampled
                          ? 'missing'
                          : selectedContainer.metrics_fresh === false
                            ? 'cached'
                            : 'live'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-lg border border-border/60 p-3">
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">CPU</div>
                        <div className="mt-1 text-lg font-semibold">
                          {selectedContainer.metrics_sampled ? formatPercent(selectedContainer.cpu_percent, 2) : '—'}
                        </div>
                      </div>
                      <div className="rounded-lg border border-border/60 p-3">
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Memory</div>
                        <div className="mt-1 text-lg font-semibold">
                          {selectedContainer.metrics_sampled ? formatPercent(selectedContainer.memory_percent, 2) : '—'}
                        </div>
                      </div>
                      <div className="rounded-lg border border-border/60 p-3">
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Memory bytes</div>
                        <div className="mt-1 text-sm font-semibold">
                          {selectedContainer.metrics_sampled
                            ? `${formatBytes(selectedContainer.memory_usage_bytes)} / ${formatBytes(selectedContainer.memory_limit_bytes)}`
                            : '—'}
                        </div>
                      </div>
                      <div className="rounded-lg border border-border/60 p-3">
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Network</div>
                        <div className="mt-1 text-sm font-semibold">
                          RX {selectedContainer.metrics_sampled ? formatBytes(selectedContainer.network_rx_bytes) : '—'}
                          <br />
                          TX {selectedContainer.metrics_sampled ? formatBytes(selectedContainer.network_tx_bytes) : '—'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="font-semibold">Matched durable alerts</h4>
                      <Badge tone={selectedContainerDurableAlerts.length > 0 ? 'critical' : 'healthy'}>
                        {selectedContainerDurableAlerts.length}
                      </Badge>
                    </div>
                    {selectedContainerDurableAlerts.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                        No persisted alert currently matches this container by container name, app, or user.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {selectedContainerDurableAlerts.map((alert) => (
                          <div key={alert.alert_key} className={cn('rounded-xl border p-4', tonePanelClasses(getAlertTone(alert)))}>
                            <div className="flex items-center justify-between gap-3">
                              <Badge tone={getAlertTone(alert)}>{alert.severity}</Badge>
                              <span className="text-xs text-muted-foreground">{formatTimestamp(alert.computed_at)}</span>
                            </div>
                            <div className="mt-2 text-sm font-medium">{getAlertTitle(alert)}</div>
                            <div className="mt-1 text-sm text-muted-foreground">{getAlertDetail(alert)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-6 rounded-xl border border-border bg-background/60 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="font-semibold">Durable resource history</h4>
                      <Badge
                        tone={
                          containerHistoryError
                            ? 'critical'
                            : containerHistoryLoading
                              ? 'warning'
                              : containerHistoryChart.length > 0
                                ? 'healthy'
                                : 'neutral'
                        }
                      >
                        {containerHistoryError
                          ? 'error'
                          : containerHistoryLoading
                            ? 'loading'
                            : `${containerHistoryChart.length} points`}
                      </Badge>
                    </div>
                    {containerHistoryError ? (
                      <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
                        {containerHistoryError}
                      </div>
                    ) : containerHistoryChart.length === 0 && containerHistoryLoading ? (
                      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading persisted container history...
                      </div>
                    ) : containerHistoryChart.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                        No persisted history has been recorded for this container yet.
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 grid grid-cols-2 gap-3">
                          <div className="rounded-lg border border-border/60 p-3">
                            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Last sample</div>
                            <div className="mt-1 text-sm font-semibold">
                              {typeof latestContainerHistoryPoint?.timestamp === 'string'
                                ? formatTimestamp(latestContainerHistoryPoint.timestamp)
                                : '—'}
                            </div>
                          </div>
                          <div className="rounded-lg border border-border/60 p-3">
                            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Latest restart count</div>
                            <div className="mt-1 text-sm font-semibold">
                              {typeof latestContainerHistoryPoint?.restart_count === 'number'
                                ? String(latestContainerHistoryPoint.restart_count)
                                : '—'}
                            </div>
                          </div>
                        </div>
                        <TrendChart
                          data={containerHistoryChart}
                          lines={[
                            { dataKey: 'cpu_percent', label: 'CPU %', color: CHART_PALETTE[0] },
                            { dataKey: 'memory_percent', label: 'Memory %', color: CHART_PALETTE[4] },
                            { dataKey: 'restart_count', label: 'Restarts', color: CHART_PALETTE[3] },
                          ]}
                          height={220}
                        />
                      </>
                    )}
                  </div>

                  <div className="mt-6 space-y-4">
                    <div>
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="font-semibold">Matched websocket incidents</h4>
                        <Badge tone={selectedContainerWebsocketEvents.length > 0 ? 'critical' : 'healthy'}>
                          {selectedContainerWebsocketEvents.length}
                        </Badge>
                      </div>
                      {selectedContainerWebsocketEvents.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                          No recent websocket incident matched this container by target host or app path.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {selectedContainerWebsocketEvents.map((event, index) => {
                            const description = describeWebsocketEvent(event)
                            return (
                              <div key={`${event.timestamp}-${event.event}-${index}`} className="rounded-xl border border-border bg-background/60 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <Badge tone={description.tone}>{description.title}</Badge>
                                  <span className="text-xs text-muted-foreground">{formatTimestamp(event.timestamp)}</span>
                                </div>
                                <div className="mt-2 text-sm text-muted-foreground">{description.detail}</div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="font-semibold">Matched client incidents</h4>
                        <Badge tone={selectedContainerClientEvents.length > 0 ? 'critical' : 'healthy'}>
                          {selectedContainerClientEvents.length}
                        </Badge>
                      </div>
                      {selectedContainerClientEvents.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                          No recent client incident matched this container by app or user context.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {selectedContainerClientEvents.map((event, index) => {
                            const description = describeClientEvent(event)
                            return (
                              <div key={`${event.timestamp}-${event.event_type}-${index}`} className="rounded-xl border border-border bg-background/60 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <Badge tone={description.tone}>{event.severity}</Badge>
                                  <span className="text-xs text-muted-foreground">{formatTimestamp(event.timestamp)}</span>
                                </div>
                                <div className="mt-2 text-sm font-medium">{description.title}</div>
                                <div className="mt-1 text-sm text-muted-foreground">{description.detail}</div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                  Select a container to inspect its recent signals and related incident context.
                </div>
              )}
            </aside>
          </div>

          <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">WebSocket failure context</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Focused on connect failures and abnormal closes from the recent telemetry buffer.
                  </p>
                </div>
                <Badge tone={wsIncidentEvents.length > 0 ? 'critical' : 'healthy'}>
                  {wsIncidentEvents.length} recent incidents
                </Badge>
              </div>
              <div className="mb-4 flex flex-wrap gap-2">
                {topEntries(snapshot.websocket.connect_failures_by_reason, 4).map(([reason, count]) => (
                  <Badge key={reason} tone="critical">{reason}: {count}</Badge>
                ))}
                {topEntries(snapshot.websocket.close_initiator_counts, 3).map(([initiator, count]) => (
                  <Badge key={initiator} tone="warning">{initiator}: {count}</Badge>
                ))}
                {topEntries(snapshot.websocket.backend_close_code_counts, 2).map(([code, count]) => (
                  <Badge key={code} tone="warning">backend {code}: {count}</Badge>
                ))}
              </div>
              <HotspotList
                groups={websocketGroups}
                emptyText="No websocket failure-class incidents are buffered right now."
              />
            </div>

            <div className="rounded-xl border border-border bg-card p-5">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">Client failure context</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Grouped by app, panel, and event type so operator impact is visible without reading raw logs.
                  </p>
                </div>
                <Badge tone={clientIncidentEvents.length > 0 ? 'critical' : 'healthy'}>
                  {clientIncidentEvents.length} recent incidents
                </Badge>
              </div>
              <div className="mb-4 flex flex-wrap gap-2">
                {topEntries(snapshot.client.severity_counts, 3).map(([severity, count]) => (
                  <Badge key={severity} tone={severity === 'error' ? 'critical' : severity === 'warning' ? 'warning' : 'neutral'}>
                    {severity}: {count}
                  </Badge>
                ))}
                {topEntries(snapshot.client.events_by_type, 4).map(([eventType, count]) => (
                  <Badge key={eventType} tone="warning">{eventType}: {count}</Badge>
                ))}
                <Badge tone={snapshot.system_health.client_terminal_recovery_events_total > 0 ? 'warning' : 'neutral'}>
                  recoveries: {snapshot.system_health.client_terminal_recovery_events_total}
                </Badge>
              </div>
              <HotspotList
                groups={clientGroups}
                emptyText="No recent client incidents are buffered right now."
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
